/**
 * Darstellung von Zahlen, die in der Oberfläche gelesen werden.
 */
import { DEFAULT_LANGUAGE } from './i18n/index.js';
/** Was `toLocaleString` je Sprache erwartet — Dezimalkomma oder Dezimalpunkt. */
const LOCALES = { de: 'de-DE', en: 'en-GB' };
/**
 * Dateigrößen in der Schreibweise, die Betriebssysteme benutzen (1 MB =
 * 1.000.000 Bytes, nicht 1.048.576).
 *
 * Bewusst so: Wer die Belegung mit dem vergleicht, was sein Rechner über
 * dieselben Dateien sagt, soll dieselbe Zahl sehen. Ab einem Gigabyte eine
 * Nachkommastelle, darunter keine — „1,2 GB" ist eine Auskunft, „937,4 MB"
 * eine Nachkommastelle zu viel.
 *
 * Die Sprache entscheidet nur über das Dezimalzeichen: „1,2 GB" gegen „1.2 GB".
 * Die Einheiten bleiben, wie sie sind — MB heißt überall MB.
 */
export function formatBytes(bytes, language = DEFAULT_LANGUAGE) {
    const locale = LOCALES[language];
    if (!Number.isFinite(bytes) || bytes <= 0)
        return '0 MB';
    if (bytes < 100_000)
        return `< ${(0.1).toLocaleString(locale)} MB`;
    const mb = bytes / 1_000_000;
    if (mb < 1000) {
        return `${mb.toLocaleString(locale, { maximumFractionDigits: mb < 10 ? 1 : 0 })} MB`;
    }
    return `${(mb / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} GB`;
}
/**
 * Ein frei eingegebenes Maß, wie die Oberfläche es zeigt — Schriftgröße,
 * Zeilenabstand.
 *
 * Nur das Dezimalzeichen hängt an der Sprache: „1,25" gegen „1.25". Keine
 * Tausenderpunkte, denn diese Zahlen stehen in schmalen Eingabefeldern.
 */
export function formatNumber(value, language = DEFAULT_LANGUAGE, maximumFractionDigits = 2) {
    if (!Number.isFinite(value))
        return '';
    return value.toLocaleString(LOCALES[language], { maximumFractionDigits, useGrouping: false });
}
/**
 * Gegenstück zu `formatNumber`.
 *
 * Komma und Punkt gelten beide, unabhängig von der eingestellten Sprache: Auf
 * dem Ziffernblock liegt ein Komma, in der oberen Reihe ein Punkt, und wer eine
 * Schriftgröße eintippt, denkt über keines von beidem nach.
 */
export function parseNumber(text) {
    const bereinigt = text.replace(',', '.').trim();
    if (bereinigt === '')
        return null;
    const wert = Number(bereinigt);
    return Number.isFinite(wert) ? wert : null;
}
/** Datum und Uhrzeit, wie die Board-Übersicht und der Verlauf sie zeigen. */
export function dateTimeFormat(language) {
    return new Intl.DateTimeFormat(LOCALES[language], {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}
//# sourceMappingURL=format.js.map